import { type FC, type ReactNode, useMemo } from "react";
import { useInView } from "react-intersection-observer";

import { IconX } from "@btnet/blueprint-icons";
import { BTText, type TBTStatusBadgeDisplayTypes } from "@btnet/core-components";
import { BTPopover } from "@btnet/core-components/BTPopover";
import { cn } from "@btnet/utilities-core";
import { useWebviewContext } from "@btnet/utilities-webview";
import { useIsMobileViewportWebview } from "@btnet/utilities-webview/useIsMobileViewportWebview";

import { BdsContentHeader } from "@buildertrend/components";

import { type ITrackingProp, track } from "utilities/analytics/analytics";
import { isInIframe } from "utilities/url/url";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { BTItemHeader } from "commonComponents/btWrappers/BTItemHeader/BTItemHeader";
import { type TBTItemHeaderIcon } from "commonComponents/btWrappers/BTItemHeader/BTItemHeader.types";
import { type IModalConfiguration } from "commonComponents/btWrappers/BTModal/BTModal";
import {
    EntityCopyLinkModalContent,
    EntityShareButtonBDS,
    type ISharingConfig,
} from "commonComponents/entity/sharing/EntityShareButton/EntityShareButton";
import { FocusProvider } from "commonComponents/utilities/Focus/FocusProvider";

import "./BTModalLayoutBDS.less";

/**
 * Container-query utilities that flip the header row into a stack. Exported so the test
 * asserts one source of truth (same pattern as `Frame.tsx`'s `FRAME_SLOT_PADDING`).
 * Must stay literal source text — Tailwind v4 only emits classes it can find verbatim.
 */
export const STACKED_HEADER_LAYOUT_CLASS =
    "@max-sm/bt-modal-layout-bds:flex-col @max-sm/bt-modal-layout-bds:items-start";

/**
 * Hook class for the `.less` rules both header branches need. Must sit on a DESCENDANT of
 * `.bt-modal-layout-bds` — a container query cannot match the element establishing the container.
 */
export const STACKED_HEADER_CLASS = "bt-modal-layout-bds-header--stacked";

const STACKED_HEADER_TEXT_WRAP = "@max-sm/bt-modal-layout-bds:whitespace-normal";

interface IBTModalLayoutBDSBaseProps {
    /**
     * A boolean to determine if the modal should use the BDS layout or the legacy layout.
     * @default false
     */
    useBDSLayout?: boolean;
    /**
     * The header for the modal layout component/ use this to override the default header.
     * @default undefined
     */
    header?: ReactNode;
    /**
     * The title of the modal
     */
    title?: string;
    /**
     * The subtitle of the modal
     */
    subtitle?: string;
    /**
     * The sharing configuration for the modal
     */
    sharing?: ISharingConfig;
    /**
     * The footer content for the modal
     */
    footerContent?: ReactNode;
    /**
     * The configuration for the modal
     */
    modalConfig?: IModalConfiguration;
    /**
     * The class name for the modal
     */
    className?: string;
    /**
     * Controls whether the close button and click-outside-to-close mask are enabled.
     * The mask is hidden in any webview context regardless of this value, and the close button is
     * hidden in a phone-width webview where native back is the dismissal affordance.
     * @default true
     */
    closable?: boolean;
    /**
     * The children nodes
     */
    children: ReactNode;
    /**
     * A boolean to determine if the BDS layout content should have padding
     * @default true
     */
    useBDSLayoutContentPadding?: boolean;
    /**
     * The status message to be displayed in status badge of the header.
     * @default undefined
     */
    statusMessage?: string;
    /**
     * The test id for the status badge. This is required if you want to display a status badge.
     * @default undefined
     */
    statusTestId?: string;
    /**
     * The type of status badge to be displayed.
     * @default undefined
     */
    statusType?: TBTStatusBadgeDisplayTypes;
    additionalHeaderContent?: ReactNode;
    layoutRef?: React.RefObject<HTMLDivElement>;
    headerRef?: React.RefObject<HTMLDivElement>;
    /**
     * Optional array of icon action configurations for the header.
     */
    iconActions?: TBTItemHeaderIcon[];
    /**
     * Opt in to stacking the header onto separate lines — job name, title, status, then
     * actions — when the `bt-modal-layout-bds` container is narrower than 576px.
     *
     * Only takes effect on the `useBDSLayout` path. The legacy `.BTModalLayoutBDS` branch
     * establishes no container, so the query never matches and the prop is inert there.
     * @default false
     */
    stackHeaderOnNarrowWidth?: boolean;
}

/**
 * `useBTItemHeader` gates the new BTItemHeader rendering path. When true, `testId`
 * must be supplied so the new header has a stable test hook; when false/undefined the
 * legacy BdsContentHeader path is used and `testId` remains optional.
 */
type IBTModalLayoutHeaderVariant =
    | {
          useBTItemHeader: true;
          testId: string;
      }
    | {
          useBTItemHeader?: false;
          testId?: string;
      };

export type IBTModalLayoutBDSProps = IBTModalLayoutBDSBaseProps & IBTModalLayoutHeaderVariant;

/**
 * The modal layout component built using the BDS components.
 * This component is used to create a modal layout with a header, content, and footer.
 *
 * @param useBDSLayout - A boolean to determine if the modal should use the BDS layout or the legacy layout.
 * @param title - The title of the modal.
 * @param subtitle - The subtitle of the modal.
 * @param sharing - The sharing configuration for the modal.
 * @param footerContent - The footer content for the modal.
 * @param modalConfig - The modal configuration for the modal.
 * @param className - The class name for the modal.
 * @param closable - A boolean to determine if the modal is closable.
 * @param useBDSContentPadding - A boolean to determine if the BDS layout content should have padding.
 *
 */

export const BTModalLayoutBDS: FC<IBTModalLayoutBDSProps & ITrackingProp> = track({})((props) => {
    const {
        useBDSLayout = false,
        header,
        className,
        closable,
        title,
        subtitle,
        sharing,
        modalConfig,
        footerContent,
        children,
        tracking,
        useBDSLayoutContentPadding = true,
        statusMessage,
        statusTestId,
        statusType,
        additionalHeaderContent,
        layoutRef,
        headerRef,
        testId,
        iconActions,
        useBTItemHeader = false,
        stackHeaderOnNarrowWidth = false,
    } = props;
    const webviewContext = useWebviewContext();
    // Width-aware, unlike the mask's `webviewContext` check: a tablet-width webview still renders
    // this as a floating card with room for its own X, and dropping it there would leave no way out.
    const isMobileViewportWebview = useIsMobileViewportWebview();
    const cleanedTitle = title ? `-${title.replace(/[^a-zA-Z0-9]/g, "")}` : "";
    const { ref: topRef, inView: topInView } = useInView();
    const { ref: bottomRef, inView: bottomInView } = useInView();

    const defaultCloseable = closable ?? true;
    const modalMask = (
        <div
            className="BTModalMaskBDS"
            onClick={() => {
                modalConfig?.beforeClose();
                tracking?.trackEvent({
                    event: "ModalClose",
                    extraInfo: {
                        isFromClickOut: true,
                    },
                });
            }}
        />
    );

    let statusProps = {};

    if (statusMessage && statusTestId && statusType) {
        statusProps = {
            statusMessage,
            statusTestId,
            statusType,
        };
    }

    const defaultHeader = (
        <BdsContentHeader
            testid="modalHeader"
            jobName={subtitle ?? ""}
            title={title ?? ""}
            slotNoWrap
            innerRef={headerRef}
            slot={
                <div
                    className="flex grow flex-row items-center justify-end gap-1"
                    data-testid="modalActionBar"
                >
                    {defaultCloseable && (
                        <div
                            className="flex flex-row items-center gap-1"
                            data-testid="modalHeaderActionBarButtonGroup"
                        >
                            <EntityShareButtonBDS
                                id={`entityShareButtonBds${cleanedTitle}`}
                                sharing={sharing}
                            />
                            {additionalHeaderContent}
                            {!isMobileViewportWebview && (
                                <BTPopover content="Close" trigger="hover">
                                    <BTButton
                                        id={`closeButton${cleanedTitle}`}
                                        data-testid="close"
                                        hotkey="close"
                                        icon={<IconX />}
                                        aria-label="Close"
                                        type="tertiary"
                                        onClick={modalConfig?.beforeClose}
                                    />
                                </BTPopover>
                            )}
                        </div>
                    )}
                </div>
            }
            {...statusProps}
        />
    );

    const beforeClose = modalConfig?.beforeClose;

    const modalIconActions = useMemo<TBTItemHeaderIcon[]>(() => {
        const actions: TBTItemHeaderIcon[] = [];

        if (sharing) {
            actions.push({
                icon: "share",
                popoverContent: <EntityCopyLinkModalContent sharing={sharing} />,
                inMenuMdBreakpoint: true,
                onClick: () => {},
            });
        }

        if (iconActions) {
            actions.push(...iconActions);
        }

        if (defaultCloseable && !isMobileViewportWebview) {
            actions.push({
                icon: "closeX",
                onClick: () => beforeClose?.(),
                pinned: true,
            });
        }

        return actions;
    }, [beforeClose, defaultCloseable, iconActions, isMobileViewportWebview, sharing]);

    const modalItemHeader = (
        <div ref={headerRef}>
            <BTItemHeader
                className="p-4 bt-modal-item-header"
                testid={`${testId ?? "bt"}-modal-item-header`}
                ariaLabel="header"
            >
                <div
                    data-testid="modalItemHeaderLayout"
                    className={cn("flex flex-row items-center", {
                        [STACKED_HEADER_LAYOUT_CLASS]: stackHeaderOnNarrowWidth,
                    })}
                >
                    <div className="flex flex-col">
                        {subtitle && (
                            <BTText
                                size="distinct-sm"
                                className={cn("whitespace-nowrap", {
                                    [STACKED_HEADER_TEXT_WRAP]: stackHeaderOnNarrowWidth,
                                })}
                            >
                                {subtitle}
                            </BTText>
                        )}
                        {title && (
                            <BTText
                                data-testid="modalTitle"
                                size="distinct-lg"
                                className={cn("whitespace-nowrap", {
                                    [STACKED_HEADER_TEXT_WRAP]: stackHeaderOnNarrowWidth,
                                })}
                            >
                                {title}
                            </BTText>
                        )}
                        {statusMessage && statusType && statusTestId && (
                            <BTItemHeader.Metadata
                                badgeText={statusMessage}
                                badgeDisplayType={statusType}
                                testid={statusTestId}
                            />
                        )}
                    </div>
                    <BTItemHeader.ActionButtons
                        testid="bt-item-header-action-buttons"
                        iconActions={modalIconActions}
                    />
                </div>
            </BTItemHeader>
        </div>
    );

    const getTitleOrHeader = () => {
        if (header) {
            return header;
        } else if (useBTItemHeader) {
            return modalItemHeader;
        } else if (title) {
            return defaultHeader;
        } else {
            return null;
        }
    };

    const headerContent = getTitleOrHeader();

    const model = (
        <>
            {useBDSLayout ? (
                <>
                    <div
                        data-testid={testId ?? "modalLayout"}
                        className={cn("bds bt-modal-layout-bds flex flex-col w-full", className, {
                            "bt-modal-layout-bds--header-shadow": !topInView,
                            "bt-modal-layout-bds--footer-shadow": !bottomInView,
                        })}
                    >
                        {headerContent && (
                            <div
                                className={cn("bt-modal-layout-bds-header", {
                                    [STACKED_HEADER_CLASS]: stackHeaderOnNarrowWidth,
                                })}
                            >
                                {headerContent}
                            </div>
                        )}

                        <div
                            className={cn("bt-modal-layout-bds-content flex-1 overflow-y-auto", {
                                "bt-modal-layout-bds-content--no-padding":
                                    !useBDSLayoutContentPadding,
                            })}
                            data-testid="modalContent"
                            ref={layoutRef}
                        >
                            <div ref={topRef} />
                            {children}
                            <div ref={bottomRef} />
                        </div>

                        {footerContent && (
                            <div
                                data-testid="modalFooter"
                                className="flex flex-row bt-modal-layout-bds-footer items-center"
                            >
                                {footerContent}
                            </div>
                        )}
                    </div>
                    {closable !== false && !webviewContext && modalConfig?.beforeClose && modalMask}
                </>
            ) : (
                <div
                    className={cn("BTModalLayoutBDS", className, {
                        HeaderShadow: !topInView,
                    })}
                    data-testid={testId ?? "modalLayout"}
                    ref={layoutRef}
                >
                    {headerContent}
                    <div className="ModalContentContainer">
                        <div ref={topRef} />
                        {children}
                        <div ref={bottomRef} />
                    </div>
                    {footerContent && (
                        <div
                            className={cn("BTModalFooterBDS", {
                                FooterShadow: !bottomInView,
                            })}
                            data-testid="modalFooter"
                        >
                            {footerContent}
                        </div>
                    )}
                    {closable !== false && !webviewContext && modalConfig?.beforeClose && modalMask}
                </div>
            )}
        </>
    );
    return isInIframe() ? <FocusProvider>{model}</FocusProvider> : model;
});
